/**
 * mk-scroll-ball — drives the scroll indicator.
 *
 * NOT a `Component` subclass, for the same reason as the loader: it is
 * rendered from layout/theme.liquid, sits outside every `.shopify-section`, and
 * is therefore never touched by `morphSection()`. `Component` exists to survive
 * a morph — preserving the instance, re-syncing `this.refs` after the children
 * are replaced — and none of that applies here. `updatedCallback` would never
 * fire either. A plain module with one engine subscription is the whole job.
 *
 * NEVER READS window.scrollY. It is permanently 0 at >=990px, where
 * `.page-wrapper` scrolls instead of the document — a ball driven from it would
 * work on mobile and be silently dead on desktop.
 *
 * Position comes from the engine's callback. The scrollable EXTENT comes from
 * Horizon's own `getScrollContainer()`, which returns whichever element is
 * actually scrolling on this side of the breakpoint. The static build computed
 * it from `document.documentElement.scrollHeight - window.innerHeight`, which
 * above 990px is <= 0 — the guard then returned early and the ball never moved.
 */

import { subscribe } from '@theme/mk-scroll-engine';
import {
  getScrollContainer,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const indicator = document.querySelector('[data-mk-scroll-indicator]');
const ball = indicator?.querySelector('[data-mk-scroll-ball]');
const trace = indicator?.querySelector('[data-mk-scroll-trace]');

if (indicator && ball) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Read the diameter from the token rather than keeping a second copy of it.
   * The stylesheet derives the ball's centring offsets from the same token, so
   * a divergence between the two would desync the ball from its own track.
   */
  const diameter = (() => {
    const parsed = parseFloat(getComputedStyle(ball).getPropertyValue('--mk-scroll-ball-size'));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 28;
  })();

  const radius = diameter / 2;
  const DEG_PER_RAD = 180 / Math.PI;

  const render = () => {
    const container = getScrollContainer();
    const max = Math.max(0, container.scrollHeight - container.clientHeight);
    if (max <= 0) return;

    const progress = Math.min(1, Math.max(0, container.scrollTop / max));

    // Stop the ball at the right edge rather than half off it.
    const travel = progress * (window.innerWidth - diameter);

    if (trace) trace.style.width = `${travel}px`;

    // Rotation is tied to travel: theta = distance / radius. Withheld entirely
    // under reduced motion — the ball slides, it does not spin.
    ball.style.transform = reducedMotion
      ? `translateX(${travel}px)`
      : `translateX(${travel}px) rotate(${(travel / radius) * DEG_PER_RAD}deg)`;
  };

  if (reducedMotion) {
    // The engine DISABLES its loop under reduced motion by design — its
    // contract is one call at subscribe, then silence — so subscribing here
    // would leave the ball frozen at translateX(0) forever. Measured exactly
    // that before this branch existed. Reduced motion is meant to remove the
    // ROLL, not the indicator: the position must still track the scroll.
    //
    // So bind the scroll event directly. `getScrollEventTarget()` returns
    // whichever target is correct for the current side of the 990px
    // breakpoint, and the media query rebinds it when that changes — the same
    // pattern Horizon's own header.js uses.
    let target = getScrollEventTarget();
    target.addEventListener('scroll', render, { passive: true });

    scrollContainerMediaQuery.addEventListener('change', () => {
      target.removeEventListener('scroll', render);
      target = getScrollEventTarget();
      target.addEventListener('scroll', render, { passive: true });
      render();
    });
  } else {
    subscribe(render);
  }

  render();

  // Hidden while the loader is on screen; revealed once it has gone. Both
  // paths are covered: the event for a first load, the class for every
  // subsequent navigation where the loader was skipped before this ran.
  const reveal = () => indicator.classList.add('is-visible');

  if (
    document.documentElement.classList.contains('mk-loader-done') ||
    !document.getElementById('mk-loader')
  ) {
    reveal();
  } else {
    document.addEventListener('mk:loader-done', reveal, { once: true });
    // Backstop for the case where `mk:loader-done` is missed entirely. The
    // loader's own budget is 2700ms and its failsafe 4000ms, so this fires
    // BEFORE the loader finishes — deliberately, and harmlessly. The overlay
    // sits at z-index 9999 against this indicator's 101, so revealing early
    // just means the ball is already in place behind it when the reveal
    // collapses the overlay. Raising this to clear the failsafe would only
    // delay the indicator on the one path where the event never arrives.
    setTimeout(reveal, 1600);
  }
}
